import { z } from "zod";
import { LocalRunRecordSchema, type LocalRunRecord } from "./localRun";
import { createReleaseRehearsalDraft, ReleaseRehearsalDraftSchema, type ReleaseRehearsalDraft } from "./releaseRehearsal";

export const RELEASE_DRAFT_HISTORY_MAX_BYTES = 128 * 1024 - 512;
export const ReleaseDraftHistorySchema = z.object({ recordVersion: z.literal(1), drafts: z.array(ReleaseRehearsalDraftSchema).max(5) }).strict().superRefine((history, context) => {
  const keys = history.drafts.map(draft => JSON.stringify([draft.artistRecordId, draft.projectId, draft.runId]));
  if (new Set(keys).size !== keys.length) context.addIssue({ code: z.ZodIssueCode.custom, message: "Repeated release draft identity" });
  if (new TextEncoder().encode(JSON.stringify(history)).length > RELEASE_DRAFT_HISTORY_MAX_BYTES)
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Release draft history exceeds recovery capacity" });
});
export type ReleaseDraftHistory = z.infer<typeof ReleaseDraftHistorySchema>;

export function resolveReleaseDraft(record: LocalRunRecord, saved: unknown) {
  const valid = LocalRunRecordSchema.parse(record), history = saved == null ? { recordVersion: 1 as const, drafts: [] } : ReleaseDraftHistorySchema.parse(saved);
  const refs = valid.run.references;
  const matching = history.drafts.filter(draft => draft.projectId === refs.projectId && draft.artistRecordId === refs.artistRecordId);
  const current = matching.find(draft => draft.runId === refs.runId);
  return { history, draft: current ?? createReleaseRehearsalDraft(valid), priorDraft: matching.filter(draft => draft.runId !== refs.runId).at(-1) ?? null };
}

/** Capacity errors leave the caller's previous exportable history unchanged. */
export function saveReleaseDraft(history: ReleaseDraftHistory, draft: ReleaseRehearsalDraft): ReleaseDraftHistory {
  const old = ReleaseDraftHistorySchema.parse(history), valid = ReleaseRehearsalDraftSchema.parse(draft);
  const position = old.drafts.findIndex(d => d.projectId === valid.projectId && d.artistRecordId === valid.artistRecordId && d.runId === valid.runId);
  if (position < 0) old.drafts.push(valid); else old.drafts[position] = valid;
  return ReleaseDraftHistorySchema.parse(old);
}

/** Explicit field carry-forward does not carry clearance or original assessment. */
export function carryReleaseDraft(record: LocalRunRecord, prior: ReleaseRehearsalDraft): ReleaseRehearsalDraft {
  const fresh = createReleaseRehearsalDraft(record), old = ReleaseRehearsalDraftSchema.parse(prior);
  if (old.projectId !== fresh.projectId || old.artistRecordId !== fresh.artistRecordId) throw new Error("Cannot carry a draft from a different project");
  return ReleaseRehearsalDraftSchema.parse({ ...old, projectId: fresh.projectId, artistRecordId: fresh.artistRecordId, runId: fresh.runId,
    rights: { ...old.rights, state: "not_provided" }, disclosure: { ...old.disclosure, state: "not_provided" }, downstream: fresh.downstream });
}
